import { createFileRoute } from '@tanstack/react-router';
import type { IconName } from 'lucide-react/dynamic';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  type CreatedSoldier,
  SoldierCreatedDialog,
} from '@/components/ui/admin/soldier-created-dialog';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { Form, FormContent, FormFooter } from '@/components/ui/form';
import { Icon } from '@/components/ui/icon';
import { ContentLoader } from '@/components/ui/loaders';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { fieldDefaults, ScreenForm } from '@/lib/screen-renderer';
import { useDynamicCreate } from '@/services/dynamic-resource';
import { useScreenByResource } from '@/services/screens';
import type { ValidationError } from '@/types/api/validation-error';

export const Route = createFileRoute('/_app/$resource/create')({
  head: () => meta({ label: 'Novo' }),
  component: RouteComponent,
});

function RouteComponent() {
  const { resource } = Route.useParams();
  const router = useRouter();

  const { data: screen, isLoading: loadingScreen } = useScreenByResource(resource);
  const [createdSoldier, setCreatedSoldier] = useState<CreatedSoldier | null>(null);
  const { mutate: save, error, isPending: isSubmitting } = useDynamicCreate(resource);

  const form = useForm<Record<string, any>>({
    shouldUseNativeValidation: false,
    defaultValues: {},
  });

  const defaults = screen ? fieldDefaults(screen.layout.fields) : {};

  // `defaultValues` do useForm só é lido na montagem, antes da screen carregar.
  useEffect(() => {
    if (screen) form.reset(fieldDefaults(screen.layout.fields));
  }, [screen, form]);

  // Recruta criado pelo admin não tem aceite dos termos: em vez de voltar pra listagem, mostra o
  // modal com o link de aceite (e o atalho do WhatsApp). Os demais recursos voltam pra listagem.
  const handleSubmit = (data: Record<string, any>) =>
    save(data, {
      onSuccess: (created: any) => {
        const id = created?.id ?? created?.data?.id;
        if (resource === 'soldiers' && id)
          setCreatedSoldier({ ...data, id } as CreatedSoldier);
        else router.back();
      },
    });

  useEffect(() => {
    if (error && (error as any).status === 422) {
      const { errors } = (error as any).value as ValidationError<Record<string, any>>;

      if (errors) {
        Object.entries(errors).forEach(([key, message]) => {
          form.setError(key, { message: message as string });
        });
      }
    }
  }, [error, form]);

  if (loadingScreen || !screen) return <ContentLoader show />;

  const label = `Novo(a) ${screen.label}`;

  return (
    <Content
      breadcrumbs={{
        title: label,
        icon: screen.icon as IconName,
        items: [
          { label: 'Listagem', route: `/${resource}` },
          { label: 'Novo', active: true },
        ],
      }}
    >
      <Form
        id="form"
        title={`Dados do ${screen.label}`}
        description={`Informe os dados do ${screen.label.toLowerCase()}.`}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <ScreenForm
            fields={screen.layout.fields}
            control={form.control}
            mode="create"
            resource={resource}
          />
        </FormContent>
        <FormFooter>
          <Button
            type="button"
            disabled={isSubmitting}
            variant="outline"
            size="lg"
            className="rounded-full"
            onClick={() => form.reset(defaults)}
          >
            Limpar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="sand"
            size="lg"
            form="form"
            className="flex items-center gap-2"
          >
            <Icon name="save" />
            Salvar
          </Button>
        </FormFooter>
      </Form>
      <SoldierCreatedDialog
        soldier={createdSoldier}
        onList={() => router.push(`/${resource}`)}
        onNew={() => {
          setCreatedSoldier(null);
          form.reset(defaults);
        }}
      />
    </Content>
  );
}
